import { photoFillImageTransform } from '@/lib/photos/photo-fill-transform'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PhotoFillSizing } from '../hooks/use-photo-fill-sizing'

interface ShapePhotoFillProps {
  shapeId: string
  shapePath: string
  photoFill: NonNullable<ShapeData['photoFill']>
  sizing: PhotoFillSizing
}

/** A photo clipped to the shape's outline, panned/zoomed/flipped/rotated by its fill settings. */
export function ShapePhotoFill({ shapeId, shapePath, photoFill, sizing }: ShapePhotoFillProps) {
  const { frameSize, coveredSize } = sizing
  const clipId = `photo-fill-clip-${shapeId}`

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <path d={shapePath} />
        </clipPath>
      </defs>
      {/*
        The clip must live on a `<g>` that does NOT carry the pan/zoom `transform` below —
        `clip-path` in `userSpaceOnUse` (the default) is evaluated in the coordinate system the
        referencing element establishes for itself, so a clip-path placed on the same (or a
        descendant of the) transformed element gets panned/zoomed right along with the image,
        letting cropped-out content spill back outside the frame the instant offsetX/offsetY/scale
        go non-identity (e.g. after a non-corner "crop" resize). Keeping the clip on this
        untransformed outer `<g>` fixes it to the frame; only the inner `<g>` (and the image inside
        it) pans/zooms.
      */}
      <g clipPath={`url(#${clipId})`}>
        <g
          transform={photoFillImageTransform({
            // `frameSize` is the larger of `shape.width/height` (what was saved/legacy cover-fits
            // against) and the shell bounds (guards the resize-lag case where the measured bbox is
            // momentarily larger) — see `helpers/photo-fill-frame.ts`.
            frameWidth: frameSize.width,
            frameHeight: frameSize.height,
            offsetX: photoFill.offsetX,
            offsetY: photoFill.offsetY,
            // Never render below native cover-fit (scale 1) — `scale` < 1 would zoom the photo out
            // past the frame's edges and expose empty canvas, regardless of how offsetX/offsetY
            // are clamped. This also self-heals any shape whose persisted scale fell below 1 from
            // an earlier bug, without needing another resize.
            scale: Math.max(photoFill.scale, 1),
            coveredWidth: coveredSize?.coveredWidth,
            coveredHeight: coveredSize?.coveredHeight,
            flippedX: photoFill.flippedX,
            flippedY: photoFill.flippedY,
            rotation: photoFill.rotation,
          })}
        >
          <image
            href={photoFill.previewUrl}
            x={0}
            y={0}
            width={coveredSize?.coveredWidth ?? frameSize.width}
            height={coveredSize?.coveredHeight ?? frameSize.height}
            preserveAspectRatio="xMidYMid slice"
            pointerEvents="none"
          />
        </g>
      </g>
    </>
  )
}
